import { platformLabel } from '@cp/shared';

/**
 * Status presentation (TZ section 14).
 *
 * Status is never carried by colour alone. Roughly one man in twelve cannot
 * separate red from green, and an operator deciding whether to restore a site
 * should not be one of the people the interface fails.
 *
 * Kept as data rather than JSX so the mapping can be asserted in a test: this is
 * the sort of rule that quietly rots when someone adds a state.
 *
 * Labels are Russian because the interface is (TZ section 18); identifiers,
 * comments and the status keys themselves stay English.
 */

export type StatusTone = 'healthy' | 'degraded' | 'down' | 'unknown' | 'maintenance' | 'pending';

export interface StatusPresentation {
  readonly tone: StatusTone;
  /** Shown next to the indicator. Never the only thing distinguishing two states. */
  readonly label: string;
  /** A shape, so the state survives greyscale and colour blindness. */
  readonly glyph: string;
  /** What an operator should read into it. */
  readonly hint: string;
}

export const SITE_HEALTH: Record<string, StatusPresentation> = {
  healthy: { tone: 'healthy', label: 'Работает', glyph: '●', hint: 'Отдаёт страницы, отклонений нет' },
  degraded: {
    tone: 'degraded',
    label: 'С отклонениями',
    glyph: '▲',
    hint: 'Ещё отдаёт страницы, но что-то требует внимания',
  },
  down: { tone: 'down', label: 'Не отвечает', glyph: '■', hint: 'Сайт не отдаёт страницы' },
  unknown: {
    tone: 'unknown',
    label: 'Нет данных',
    glyph: '?',
    // The distinction that matters: no data is not evidence of failure.
    hint: 'Нет свежих данных от агента: это не то же самое, что отказ',
  },
  maintenance: {
    tone: 'maintenance',
    label: 'Обслуживание',
    glyph: '◐',
    hint: 'Намеренно выключен, оповещения приглушены',
  },
};

/** The lifecycle from SITE_STATUSES in the domain layer, in operators' words. */
export const SITE_STATUS: Record<string, StatusPresentation> = {
  draft: { tone: 'pending', label: 'Черновик', glyph: '○', hint: 'Заведён, но ещё не разворачивался' },
  provisioning: { tone: 'pending', label: 'Разворачивается', glyph: '◐', hint: 'Идёт первичная установка' },
  ready: { tone: 'healthy', label: 'Готов', glyph: '●', hint: 'Развёрнут и обслуживается' },
  suspended: { tone: 'degraded', label: 'Приостановлен', glyph: '▲', hint: 'Остановлен, данные на месте' },
  archived: { tone: 'unknown', label: 'В архиве', glyph: '▢', hint: 'Выведен из работы, данные сохранены' },
  destroying: { tone: 'maintenance', label: 'Удаляется', glyph: '↓', hint: 'Идёт снятие с сервера' },
  failed: { tone: 'down', label: 'Ошибка', glyph: '■', hint: 'Операция не завершилась' },
  importing: { tone: 'pending', label: 'Импорт', glyph: '◐', hint: 'Собирается рядом со старым сервером; домены ещё указывают туда' },
};

export const JOB_STATE: Record<string, StatusPresentation> = {
  queued: { tone: 'pending', label: 'В очереди', glyph: '○', hint: 'Ждёт свободного обработчика' },
  running: { tone: 'pending', label: 'Выполняется', glyph: '◐', hint: 'В работе' },
  retrying: { tone: 'degraded', label: 'Повтор', glyph: '↻', hint: 'Сорвалась и повторяется' },
  completed: { tone: 'healthy', label: 'Завершена', glyph: '●', hint: 'Выполнена успешно' },
  failed: { tone: 'down', label: 'Ошибка', glyph: '■', hint: 'Попытки исчерпаны, смотрите причину' },
  cancelled: { tone: 'unknown', label: 'Отменена', glyph: '×', hint: 'Остановлена до выполнения' },
};

export const NODE_STATE: Record<string, StatusPresentation> = {
  active: { tone: 'healthy', label: 'Активен', glyph: '●', hint: 'Принимает новые сайты' },
  cordoned: { tone: 'degraded', label: 'Закрыт', glyph: '▲', hint: 'Работает, но новые сайты не принимает' },
  draining: { tone: 'degraded', label: 'Разгружается', glyph: '↓', hint: 'Сайты переносятся на другие серверы' },
  offline: { tone: 'down', label: 'Недоступен', glyph: '■', hint: 'От агента нет отклика' },
  awaiting: { tone: 'pending', label: 'Ждёт агента', glyph: '○', hint: 'Сервер добавлен, агент ещё не подключился' },
};

/** DomainBindingState: whether the record in DNS matches the one we intend. */
export const DOMAIN_STATE: Record<string, StatusPresentation> = {
  pending: { tone: 'pending', label: 'Ожидает', glyph: '○', hint: 'Запись ещё не применена' },
  bound: { tone: 'healthy', label: 'Привязан', glyph: '●', hint: 'Запись совпадает с намеченной' },
  drift: {
    tone: 'degraded',
    label: 'Расхождение',
    glyph: '▲',
    // The panel reports drift and never silently corrects it: DNS belongs to
    // 2d2s, and a quiet fix here would fight whoever made the change there.
    hint: 'Запись отличается от намеченной; панель её не исправляет',
  },
  error: { tone: 'down', label: 'Ошибка', glyph: '■', hint: 'Запись проверить не удалось' },
};

/** A purchase of domains, by what 2d2s says of its batch (ADR-0039). */
export const PURCHASE_STATUS: Record<string, StatusPresentation> = {
  QUEUED: { tone: 'pending', label: 'В очереди', glyph: '○', hint: '2d2s ещё не начал проверку' },
  CHECKING_AVAILABILITY: { tone: 'pending', label: 'Проверка', glyph: '◌', hint: '2d2s проверяет, какие имена свободны' },
  SHORTLIST_READY: {
    tone: 'maintenance',
    label: 'Ждёт подтверждения',
    glyph: '◆',
    hint: 'Свободные имена найдены; ничего не куплено, пока покупку не подтвердят',
  },
  PURCHASING: { tone: 'pending', label: 'Покупка', glyph: '◌', hint: '2d2s покупает выбранные имена' },
  PROVISIONING_DNS: { tone: 'pending', label: 'Настройка DNS', glyph: '◌', hint: '2d2s создаёт зоны Cloudflare и прописывает NS' },
  COMPLETED: { tone: 'healthy', label: 'Куплено', glyph: '●', hint: 'Все выбранные имена куплены' },
  PARTIALLY_COMPLETED: { tone: 'degraded', label: 'Куплено частично', glyph: '▲', hint: 'Часть имён купить не удалось' },
  FAILED: { tone: 'down', label: 'Не удалось', glyph: '■', hint: 'Покупка не состоялась' },
  CANCELLED: { tone: 'unknown', label: 'Отменено', glyph: '—', hint: 'Покупку отменили' },
};

export const DOMAIN_ROLE_LABELS: Record<string, string> = {
  primary: 'основной',
  alias: 'алиас',
};

export const USER_STATUS: Record<string, StatusPresentation> = {
  active: { tone: 'healthy', label: 'Активен', glyph: '●', hint: 'Может входить в панель' },
  disabled: { tone: 'unknown', label: 'Отключён', glyph: '×', hint: 'Вход запрещён, сессии прекращены' },
};

const FALLBACK: StatusPresentation = {
  tone: 'unknown',
  label: 'Неизвестно',
  glyph: '?',
  hint: 'Интерфейс не знает такого состояния',
};

/**
 * Falls back visibly rather than rendering nothing. A blank cell reads as "fine"
 * and is exactly the wrong answer for a state nobody has taught the UI about.
 */
export function present(map: Record<string, StatusPresentation>, status: string): StatusPresentation {
  return map[status] ?? FALLBACK;
}

/** Role names as an operator reads them, not as the database stores them. */
export const ROLE_LABELS: Record<string, string> = {
  owner: 'Владелец',
  admin: 'Администратор',
  operator: 'Оператор',
  editor: 'Редактор',
  viewer: 'Наблюдатель',
};

/** Job types as an operation an operator recognises. The raw type stays visible
 *  in a tooltip, because that is the string they will search the logs for. */
export const JOB_TYPE_LABELS: Record<string, string> = {
  'site.create': 'Создание сайта',
  'site.provision': 'Первичная установка',
  'site.deploy': 'Развёртывание',
  'site.restart': 'Перезапуск',
  'site.upgrade': 'Обновление WordPress/PHP',
  'site.domains': 'Изменение доменов',
  'site.certificate': 'Выпуск сертификата',
  'site.relocate': 'Восстановление на другом сервере',
  'site.structure': 'Применение структуры шаблона',
  'template.capture': 'Снятие шаблона с сайта',
  'site.remnant_stop': 'Остановка прежней копии',
  'site.import': 'Импорт: подготовка сайта',
  'site.import_apply': 'Импорт: применение данных',
  'site.import_switch': 'Импорт: переключение DNS',
  'site.import_rollback': 'Импорт: возврат DNS',
  'site.restore_drill': 'Проверка восстановления',
  'site.backup': 'Резервная копия',
  'site.restore': 'Восстановление',
  'site.destroy': 'Удаление сайта',
  'site.clone': 'Клонирование',
  'site.host_clone': 'Клонирование',
  'site.host_create': 'Создание сайта из файлов',
  'site.migrate': 'Перенос на другой сервер',
  'site.release': 'Возврат после неудачного переноса',
  'site.reclaim': 'Удаление остановленной копии',
  'servers.sync': 'Обновление списка серверов',
  'sites.sync': 'Обновление сайтов по тегу 2d2s',
  'host.discover': 'Осмотр внешнего сервера',
  'site.host_restore': 'Развёртывание копии на сервере',
};

/** Audit actions as sentences an operator reads, not codes they decode. The
 *  code stays visible in the detail view for searching the logs. */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  'auth.login': 'Вход в панель',
  'auth.logout': 'Выход из панели',
  'auth.mfa': 'Проверка второго фактора',
  'mfa.offer': 'Предложена привязка приложения',
  'mfa.enroll': 'Привязка приложения',
  'mfa.reset': 'Сброс второго фактора',
  'user.create': 'Создан сотрудник',
  'user.disable': 'Доступ закрыт',
  'user.enable': 'Доступ восстановлен',
  'user.roles': 'Изменены роли',
  'user.profile': 'Изменён профиль',
  'user.password': 'Смена пароля',
  'node.enroll': 'Подключён сервер',
  'template.publish': 'Опубликован шаблон',
  'template.deprecate': 'Шаблон снят с публикации',
  'tag.create': 'Создан тег',
  'tag.update': 'Изменён тег',
  'tag.delete': 'Удалён тег',
  'site.tags': 'Изменены теги сайта',
  'site.create.requested': 'Запрошено создание сайта',
  'site.deploy.requested': 'Запрошено развёртывание',
  'site.restart.requested': 'Запрошен перезапуск',
  'site.upgrade.requested': 'Запрошено обновление WordPress/PHP',
  'site.domains.requested': 'Запрошено изменение доменов',
  'site.certificate.scheduled': 'Запланирован выпуск сертификата',
  'site.relocate.requested': 'Запрошено восстановление на другом сервере',
  'site.structure.requested': 'Запрошено применение структуры шаблона',
  'template.capture.requested': 'Запрошено снятие шаблона',
  'template.capture': 'Опубликована версия шаблона',
  'site.remnant_stop.scheduled': 'Запланирована остановка прежней копии',
  'site.import.requested': 'Запрошен импорт сайта',
  'site.import.token_issued': 'Выданы команды для старого сервера',
  'site.import.uploaded': 'Старый сервер передал сайт',
  'site.import_switch.requested': 'Запрошено переключение DNS на панель',
  'site.import_rollback.requested': 'Запрошен возврат DNS на старый сервер',
  'site.restore_drill.scheduled': 'Запланирована проверка восстановления',
  'site.restore_drill.passed': 'Проверка восстановления прошла',
  'site.restore_drill.failed': 'Проверка восстановления не прошла',
  'site.backup.requested': 'Запрошена резервная копия',
  'site.backup.scheduled': 'Копия по расписанию',
  'site.restore.requested': 'Запрошено восстановление',
  'site.destroy.requested': 'Запрошено удаление сайта',
  'site.clone.requested': 'Запрошено клонирование',
  'site.migrate.requested': 'Запрошен перенос',
  'site.bootstrap_credential.revealed': 'Показан пароль первого администратора',
  'site.migrate.released': 'Сайт возвращён на прежний сервер после неудачного переноса',
  'servers.sync.requested': 'Запрошено обновление списка серверов из 2d2s',
  'node.register': 'Подключён сервер',
  'node.enrollment_token': 'Выпущен токен подключения агента',
  'node.cordon': 'Сервер закрыт для новых сайтов',
  'node.uncordon': 'Сервер снова принимает сайты',
  'node.drain': 'Запущен перенос всех сайтов с сервера',
  'node.agent_revoked': 'Отозван ключ агента',
  'node.agent_install.started': 'Начата установка агента по SSH',
  'node.agent_install.succeeded': 'Агент установлен по SSH',
  'node.agent_install.failed': 'Установка агента по SSH не удалась',
  'panel.backup': 'Резервная копия панели',
  'site.reclaim.requested': 'Запрошено удаление остановленной копии',
  'user.delete': 'Удалена учётная запись',
  'housekeeping.pruned': 'Автоматическая очистка старых записей',
  'servers.sync': 'Обновлён список серверов из 2d2s',
  'sites.sync.requested': 'Запрошено обновление сайтов по тегу 2d2s',
  'site.observe': 'Сайт добавлен в мониторинг',
  'site.unobserve': 'Сайт убран из мониторинга',
  'site.platform.set': 'Изменены платформа или адрес админки',
  'site.credential.create': 'Добавлен доступ',
  'site.credential.update': 'Изменён доступ',
  'site.credential.delete': 'Удалён доступ',
  'site.credential.reveal': 'Показан пароль доступа',
  'site.credential.sign_in': 'Вход в админку из панели',
  'node.credential.create': 'Добавлен доступ к серверу',
  'node.credential.update': 'Изменён доступ к серверу',
  'node.credential.delete': 'Удалён доступ к серверу',
  'node.credential.reveal': 'Показан пароль доступа к серверу',
  'domain.dns.create': 'Добавлена DNS-запись',
  'domain.dns.update': 'Изменена DNS-запись',
  'domain.dns.delete': 'Удалена DNS-запись',
  'domain.ssl.set': 'Изменён режим SSL',
  'domain.always_https.set': 'Изменено «Всегда HTTPS»',
  'domain.cache.purge': 'Очищен кэш Cloudflare',
  'domain.redirect.create': 'Добавлен редирект',
  'domain.redirect.update': 'Изменён редирект',
  'domain.redirect.delete': 'Удалён редирект',
  'site.clone.traces_cleared': 'Убраны следы исходного сайта',
  'site.upload': 'Загружен архив нового сайта',
  'seo.changed': 'SEO: изменения на сайте',
  'bulk.run': 'Массовое действие',
  'bulk.stop': 'Массовое действие остановлено',
  'bulk.retry': 'Массовое действие: повтор неудавшихся',
  'seo.key_page.add': 'SEO: страница добавлена в ключевые',
  'seo.key_page.remove': 'SEO: страница убрана из ключевых',
  'domain.purchase.checked': 'Проверены домены для покупки',
  'domain.purchase.confirmed': 'Куплены домены',
  'domain.purchase.cancelled': 'Отменена покупка доменов',
  'integration.set': 'Изменена интеграция',
  'integration.remove': 'Удалена интеграция',
  'search_console.connect': 'Search Console: запрошено подключение',
  'search_console.dns': 'Search Console: запись TXT в DNS',
  'search_console.verified': 'Search Console: домен подтверждён',
  'search_console.disconnect': 'Search Console: сайт отключён',
  'content.profile.set': 'Контент: изменён профиль',
  'content.write': 'Контент: статья в очереди на написание',
  'content.reject': 'Контент: статья отклонена',
  'content.rewrite': 'Контент: статья отправлена на переписывание',
  'content.publish': 'Контент: статья отправлена в CMS',
  'content.retry': 'Контент: повтор',
  'content.published': 'Контент: статья опубликована',
  'content.drafted': 'Контент: черновик отправлен в CMS',
  'backup.download': 'Скачан файл копии',
  'host.discover.requested': 'Запрошен осмотр внешнего сервера',
  'host.discover.scheduled': 'Осмотр внешнего сервера по расписанию',
  'site.host_restore.requested': 'Запрошено развёртывание копии на сервере',
  'site.host_restore.dns_switched': 'DNS переключён на сервер, где развёрнута копия',
  'site.host_restore.dns_by_hand': 'Копия развёрнута, DNS переключается вручную',
};

export const AUDIT_ENTITY_LABELS: Record<string, string> = {
  user: 'Сотрудник',
  session: 'Сессия',
  site: 'Сайт',
  node: 'Сервер',
  tag: 'Тег',
  template: 'Шаблон',
  template_version: 'Версия шаблона',
  job: 'Задача',
  server: 'Сервер',
  system: 'Панель',
  domain_purchase: 'Покупка доменов',
  bulk_run: 'Массовое действие',
  integration: 'Интеграция',
};

export const ENGINE_LABELS: Record<string, string> = {
  wordpress: 'WordPress',
  mini_cms: 'Mini CMS',
  external: 'Внешний',
};

/**
 * What a site runs on, for its mark and its name: the panel's own engine, or
 * what a watched site was found to be.
 */
export function sitePlatform(site: { readonly engine: string; readonly platform: string | null }): { key: string | null; label: string } {
  if (site.engine === 'external') return { key: site.platform, label: platformLabel(site.platform) ?? 'Не определена' };
  return { key: site.engine, label: label(ENGINE_LABELS, site.engine) };
}

export const TIER_LABELS: Record<string, string> = {
  production: 'Рабочий',
  staging: 'Тестовый',
};

export const BACKUP_POLICY_LABELS: Record<string, string> = {
  standard: 'Стандартная',
  active: 'Активная',
  critical: 'Критичная',
  custom: 'Своя',
};

/** Translates what it recognises and passes through what it does not, so a new
 *  value from the backend shows up as itself instead of disappearing. */
export function label(map: Record<string, string>, value: string): string {
  return map[value] ?? value;
}

/**
 * An audit action as a sentence. Outcomes of operations — `site.clone.completed`,
 * `site.deploy.failed` — are named after the operation, so every job type
 * has them without a label of its own.
 */
export function auditActionLabel(action: string): string {
  const known = AUDIT_ACTION_LABELS[action];
  if (known) return known;
  const outcome = /^(.*)\.(completed|failed)$/.exec(action);
  const operation = outcome ? JOB_TYPE_LABELS[outcome[1] ?? ''] : undefined;
  if (outcome && operation) return `${operation}: ${outcome[2] === 'completed' ? 'выполнено' : 'не удалось'}`;
  return action;
}

export function roleList(roles: readonly string[]): string {
  return roles.map((role) => label(ROLE_LABELS, role)).join(', ');
}

/** Why a backup was taken. */
export const BACKUP_KIND_LABELS: Record<string, string> = {
  manual: 'вручную',
  automatic: 'по расписанию',
  pre_deploy: 'перед развёртыванием',
  pre_restore: 'перед восстановлением',
  pre_migrate: 'перед переносом',
  import: 'импорт со старого сервера',
};

/** Whether a backup was checked; `tone` is the status class it is drawn with. */
export const BACKUP_VERIFICATION: Record<string, { label: string; tone: string }> = {
  verified: { label: 'проверена', tone: 'status-healthy' },
  pending: { label: 'не проверена', tone: 'status-pending' },
  failed: { label: 'проверка не прошла', tone: 'status-down' },
};

/** Site operations, as a toast names the one just queued. */
export const OPERATION_LABELS: Record<string, string> = {
  upgrade: 'Обновление WordPress/PHP',
  domains: 'Изменение доменов',
  structure: 'Применение структуры шаблона',
  restart: 'Перезапуск',
  deploy: 'Развёртывание',
  backup: 'Резервная копия',
  restore: 'Восстановление',
  destroy: 'Удаление сайта',
};

/** What a site on an external server is, as its agent found it (ADR-0033). */
export const HOSTED_SITE_KIND_LABELS: Record<string, string> = {
  wordpress: 'WordPress',
  convertstudio: 'ConvertStudio',
  static: 'Статический',
  app: 'Приложение',
  unknown: 'Не распознан',
};

/** Cloudflare's SSL modes, as its dashboard names them. */
export const ZONE_SSL_LABELS: Record<string, string> = {
  off: 'Off',
  flexible: 'Flexible',
  full: 'Full',
  strict: 'Full (strict)',
};

/** What each role may do, in a line, beside the checkbox that grants it. */
export const ROLE_HINTS: Record<string, string> = {
  owner: 'Всё, включая управление командой и секретами',
  admin: 'Всё, кроме передачи владения',
  operator: 'Создание, деплой, копии и статьи; без удаления сайтов',
  editor: 'Просмотр сайтов и задач; статьи и работа в CMS',
  viewer: 'Только чтение',
};

/** Where an import from another server stands (P7). */
export const IMPORT_STATE_LABELS: Record<string, string> = {
  provisioning: 'Подготовка сайта на сервере',
  awaiting_upload: 'Ожидание данных со старого сервера',
  applying: 'Сборка сайта',
  ready: 'Готов к переключению DNS',
  switched: 'DNS переключён на панель',
  failed: 'Сборка не удалась — выгрузите данные заново',
};
